import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, GraduationCap } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { GalleryCard } from "@/components/GalleryCard";
import { YearFilter } from "@/components/YearFilter";
import { IMAGES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Workshops() {
  const [items, setItems] = useState(null);
  const [year, setYear] = useState("All");

  const years = items
    ? ["All", ...[...new Set(items.map((i) => i.year))].sort().reverse()]
    : ["All"];
  const visible =
    items === null ? null : year === "All" ? items : items.filter((i) => i.year === year);

  useEffect(() => {
    axios
      .get(`${API}/gallery`, { params: { club: "lekka" } })
      .then((r) => setItems(r.data))
      .catch(() => setItems([]));
  }, []);

  return (
    <div data-testid="workshops-page">
      <section className="relative overflow-hidden bg-sand grain py-16 sm:py-20">
        <img
          src={IMAGES.excursions}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover opacity-15"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-sand/30 via-sand/70 to-sand" />
        <div className="relative mx-auto max-w-5xl px-6">
          <Reveal>
            <Link
              to="/english-is-lekka"
              data-testid="workshops-back-link"
              className="inline-flex items-center gap-1.5 font-heading text-sm font-bold text-teal transition-colors hover:text-ember"
            >
              <ArrowLeft size={15} />
              Back to English is Lekka
            </Link>
            <Eyebrow>English is Lekka, teacher development</Eyebrow>
            <h1 className="mt-5 font-display text-5xl font-extrabold tracking-tight text-coal sm:text-6xl lg:text-7xl">
              <span className="text-teal">Workshops</span> behind the classroom magic
            </h1>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-7 max-w-3xl text-base leading-relaxed text-coal-muted md:text-lg">
              Strong clubs start with strong educators. English is Lekka runs hands-on teacher
              development workshops across the district, from standard setting and marking practice
              to poetry with Zubayr Charles and visual literacy with movie guru Riaz Solker.
            </p>
            <p className="mt-5 max-w-3xl text-base leading-relaxed text-coal-muted">
              More than 1,100 teacher training seats since 2022, and every one of them carries back
              into a classroom full of learners.
            </p>
            <div
              className="mt-8 inline-flex items-center gap-3 rounded-full bg-teal-subtle px-5 py-2.5"
              data-testid="workshops-stat-badge"
            >
              <GraduationCap size={18} className="text-teal" />
              <span className="font-heading text-sm font-bold text-teal">
                1,131 teacher training seats since 2022
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-teal-subtle py-14" data-testid="workshops-gallery">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <Eyebrow>Gallery</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-coal sm:text-4xl">
              Moments from the workshops
            </h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-coal-muted">
              Like, comment and share your favourite moments from our teacher development sessions.
            </p>
          </Reveal>
          {items !== null && items.length > 0 && (
            <YearFilter years={years} active={year} onChange={setYear} count={visible?.length} testIdPrefix="workshops" />
          )}
          {visible === null ? (
            <p className="mt-10 text-sm text-coal-muted" data-testid="workshops-gallery-loading">Loading gallery...</p>
          ) : visible.length === 0 ? (
            <p className="mt-10 text-sm text-coal-muted" data-testid="workshops-gallery-empty">
              {items.length === 0 ? "Workshop moments are on their way." : `No moments from ${year} yet.`}
            </p>
          ) : (
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {visible.map((item, i) => (
                <GalleryCard key={item.id} item={item} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
